import React, { FC, ReactElement } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import styled, { keyframes, css } from 'styled-components';

interface CircularLoaderProps {
  size?: number;
  centered?: boolean;
  containerMinHeight?: string;
  icon?: ReactElement;
  spinIcon?: boolean;
  testId?: string;
}

const StyledCircularProgress = styled(CircularProgress)`
  svg {
    color: ${({ theme }) => theme?.palette?.primaryColor || 'blue'};
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

interface IconWrapperProps {
  $size?: number;
  $spin?: boolean;
}

const IconWrapper = styled.div<IconWrapperProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  ${({ $spin }) =>
    $spin &&
    css`
      animation: ${spin} 1.4s linear infinite;
    `};

  /* Ensure the icon respects size prop */
  svg {
    width: ${({ $size }) => ($size ? `${$size}px` : 'inherit')};
    height: ${({ $size }) => ($size ? `${$size}px` : 'inherit')};
  }
`;

interface StyledContainerProps {
  $containerMinHeight?: string;
}

const StyledContainer = styled.div<StyledContainerProps>`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: ${({ $containerMinHeight }) => $containerMinHeight || 'inherit'};
`;

const CircularLoader: FC<CircularLoaderProps> = ({ centered, containerMinHeight, size, icon, spinIcon = false, testId = "CircularLoader"}) => {
  const content = icon ? (
    <IconWrapper $spin={spinIcon} $size={size}>
      {icon}
    </IconWrapper>
  ) : (
    <StyledCircularProgress size={size} />
  );

  return centered ? <StyledContainer $containerMinHeight={containerMinHeight} data-testid={testId}>{content}</StyledContainer> : content;
};

export default CircularLoader;
